import { endOfDay, startOfDay, subDays, subMonths, subWeeks } from "date-fns"

import type { DatePreset } from "@/types/DatePreset"

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

/**
 * Every rolling window the app offers, in one place.
 *
 * Surfaces pick the subset that suits them — traces reaches for hours, the
 * review overviews for days — but the duration and the wording behind a given
 * key are defined once, so two surfaces cannot drift into labelling different
 * spans "Last 7 days".
 *
 * The keys are persisted: insights writes the active one into the URL, so
 * renaming a key changes what a shared link resolves to.
 */
export const RELATIVE_WINDOWS = {
  "1h": { label: "Last 1 hour", compactLabel: "1 hour", durationMs: HOUR_MS },
  "3h": {
    label: "Last 3 hours",
    compactLabel: "3 hours",
    durationMs: 3 * HOUR_MS,
  },
  "12h": {
    label: "Last 12 hours",
    compactLabel: "12 hours",
    durationMs: 12 * HOUR_MS,
  },
  "1d": { label: "Last 24 hours", compactLabel: "1 day", durationMs: DAY_MS },
  "3d": {
    label: "Last 3 days",
    compactLabel: "3 days",
    durationMs: 3 * DAY_MS,
  },
  "7d": {
    label: "Last 7 days",
    compactLabel: "7 days",
    durationMs: 7 * DAY_MS,
  },
  "14d": {
    label: "Last 14 days",
    compactLabel: "14 days",
    durationMs: 14 * DAY_MS,
  },
  "30d": {
    label: "Last 30 days",
    compactLabel: "30 days",
    durationMs: 30 * DAY_MS,
  },
  "90d": {
    label: "Last 90 days",
    compactLabel: "90 days",
    durationMs: 90 * DAY_MS,
  },
} as const

export type RelativeWindowKey = keyof typeof RELATIVE_WINDOWS

export interface RelativeDatePreset extends DatePreset {
  key: RelativeWindowKey
  durationMs: number
}

/**
 * Builds the presets for a surface from the registry.
 *
 * `compact` picks the shorter wording, for grids too narrow to carry "Last".
 */
export function relativePresets(
  keys: readonly RelativeWindowKey[],
  { compact = false }: { compact?: boolean } = {},
): RelativeDatePreset[] {
  return keys.map((key) => {
    const window = RELATIVE_WINDOWS[key]
    return {
      key,
      durationMs: window.durationMs,
      label: compact ? window.compactLabel : window.label,
      getValue: () => {
        const nowMs = Date.now()
        return {
          start: new Date(nowMs - window.durationMs),
          end: new Date(nowMs),
        }
      },
    }
  })
}

// A stored range counts as a window only when its end sits close to "now" and
// its span matches that window's duration.
const RECENT_END_TOLERANCE_MS = 5 * 60 * 1000
const DURATION_MATCH_TOLERANCE_MS = 60 * 1000

/**
 * Maps a stored range back to the window that would produce it, so a range
 * picked as "Last 7 days" still reads that way when the page is reopened.
 */
export function matchRelativeWindow(
  range: { start: Date | null; end: Date | null },
  keys: readonly RelativeWindowKey[],
): RelativeDatePreset | null {
  const { start, end } = range
  if (!start || !end) {
    return null
  }
  if (Math.abs(Date.now() - end.getTime()) > RECENT_END_TOLERANCE_MS) {
    return null
  }

  const durationMs = end.getTime() - start.getTime()
  return (
    relativePresets(keys).find(
      (preset) =>
        Math.abs(durationMs - preset.durationMs) < DURATION_MATCH_TOLERANCE_MS,
    ) ?? null
  )
}

/**
 * Quick Select windows offered by every date range dropdown.
 *
 * Today runs midnight through end-of-day, so the rolling "Last 24 hours"
 * window is a separate preset rather than a duplicate of it. The calendar-
 * anchored entries stay here: they answer "which day", not "how long ago",
 * so they have no duration to register.
 */
export const DATE_PRESETS: DatePreset[] = [
  {
    label: "Today",
    getValue: () => ({
      start: startOfDay(new Date()),
      end: endOfDay(new Date()),
    }),
  },
  {
    label: "Yesterday",
    getValue: () => {
      const yesterday = subDays(new Date(), 1)
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) }
    },
  },
  ...relativePresets(["1d", "7d", "14d", "30d"]),
  {
    label: "Last week",
    getValue: () => ({ start: subWeeks(new Date(), 1), end: new Date() }),
  },
  {
    label: "Last month",
    getValue: () => ({ start: subMonths(new Date(), 1), end: new Date() }),
  },
  {
    label: "Last 3 months",
    getValue: () => ({ start: subMonths(new Date(), 3), end: new Date() }),
  },
]
